import * as React from "react";
import { useEffect } from "react";
import { appPathname } from "@/lib/platform";
import { CALL_STATUS_COLORS, CALL_STATUS_LABELS, CallStatus, FILTER_SEPARATOR, STACK_STATUSES } from "@/constants";
import { formatDate, formatDuration } from "@/utils";
import { Box, Card, Chip, Grid, IconButton, Stack, Tooltip } from "@mui/material";
import Typography from "@mui/material/Typography";
import { ChatsIcon, InfoIcon, PlayCircleIcon } from "@phosphor-icons/react";
import { Clock } from "@phosphor-icons/react/dist/ssr/Clock";
import { Phone } from "@phosphor-icons/react/dist/ssr/Phone";
import { PhoneCall } from "@phosphor-icons/react/dist/ssr/PhoneCall";
import { WarningIcon } from "@phosphor-icons/react/dist/ssr/Warning";
import { useNavigate, useSearchParams } from "react-router-dom";

import { CallLog, callLogsKeys, useCallLogsStats, useMarkCallStuck } from "@/hooks/api/use-call-logs";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { CallLogModal } from "@/components/call-log-modal/call-log-modal";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column, FilterOption, FilterType } from "@/components/customTable/types";
import { RecordingModal } from "@/components/recording-modal/recording-modal";
import { StatisticCard } from "@/components/statisticCard/statistic-card";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { AnalysingIndicator } from "../campaigns/view/lead-list/analysing-indicator";
import { useAuth } from "@/components/core/auth-provider";
import { ConfirmModal } from "@/components/confirm-modal/confirm-modal";
import { CompletionsJsonModal } from "./completions-json-modal";

export function CallLogs(): React.JSX.Element {
	const [searchParams] = useSearchParams();
  const { permissions, user } = useAuth();
	const navigate = useNavigate();
	const [stuckCallId, setStuckCallId] = React.useState<string | null>(null);
	const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
	const [selectedConversationId, setSelectedConversationId] = React.useState<string | null>(null);

	const {
		items: callLogs,
		isLoading,
		totalItems,
		refetchData: fetchCallLogs,
	} = useCachedTableStateWithUrl<CallLog>({
		queryKey: callLogsKeys.lists(),
		endpoint: "/calls",
	});

	const {
		data: selectedCallLog,
		open: isCallLogModalOpen,
		handleClose: handleCallLogModalClose,
		handleOpen: handleCallLogModalOpen,
	} = useDialog<CallLog | null>();

	const {
		data: selectedRecordingCallLog,
		open: isRecordingModalOpen,
		handleClose: handleRecordingModalClose,
		handleOpen: handleRecordingModalOpen,
	} = useDialog<CallLog | null>();

	const markCallStuckMutation = useMarkCallStuck();

	const campaignIdParamParts = searchParams.get("campaignId")?.split(FILTER_SEPARATOR);
	const campaignIdParam = campaignIdParamParts?.at(-1) || undefined;

	const { data: stats, isLoading: isStatsLoading } = useCallLogsStats(campaignIdParam);

	useEffect(() => {
		const callId = searchParams.get("play");

		if (callId) {
			handleRecordingModalOpen({ id: callId } as CallLog);
			navigate(appPathname(), { replace: true });
		}
	}, []);

	const filtersOptions: FilterOption[] = React.useMemo(() => {
		return [
			{
				name: "campaignId",
				label: "Campaign",
				value: "",
				type: FilterType.FETCH_SELECT,
				getOptionsUrl: "/campaigns",
				fetchLabel: "title",
				fetchValue: "id",
			},
			{
				name: "status",
				label: "Status",
				value: [],
				type: FilterType.SELECT,
				options: Object.values(CALL_STATUS_LABELS).map((label) => ({
					label,
					value: CallStatus[label.replace(" ", "")],
				})),
			},
		];
	}, []);

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: {
					id: "id",
					label: "ID",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "id",
				getCellValue: (row: any) => {
					return (
						<Tooltip title={row.id} arrow>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{
									fontWeight: "600",
									textOverflow: "ellipsis",
									overflow: "hidden",
									whiteSpace: "nowrap",
									maxWidth: "100px",
								}}
							>
								{row.id}
							</Typography>
						</Tooltip>
					);
				},
			},

			{
				header: {
					id: "campaign",
					label: "Campaign",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "campaign",
				getCellValue: (row: any) => {
					if (!row.campaign) return "";
					return (
						<Tooltip title="View Campaign" arrow>
							<Chip
								color="info"
								size="small"
								label={row.campaign?.title}
								onClick={() => navigate(`/campaigns/${row.campaign?.id}/view`)}
							/>
						</Tooltip>
					);
				},
			},

			{
				header: {
					id: "agent",
					label: "Agent",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "agent",
				getCellValue: (row: any) => {
					if (!row.agent) return "";
					return (
						<Tooltip title="View Agent" arrow>
							<Chip
								color="primary"
								size="small"
								label={row.agent?.name}
								onClick={() => navigate(`/agents/${row.agent?.id}`)}
							/>
						</Tooltip>
					);
				},
			},
			{
				header: {
					id: "contactNumber",
					label: "Contact Number",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "contactNumber",
			},
			{
				header: {
					id: "originNumber",
					label: "Origin Number",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "originNumber",
			},
			{
				header: {
					id: "duration",
					label: "Duration",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "duration",
				getCellValue: (row: any) => (
					<Typography variant="body2" whiteSpace="nowrap">
						{formatDuration(row.duration)}
					</Typography>
				),
			},
			{
				header: {
					id: "direction",
					label: "Direction",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "direction",
				getCellValue: (row: any) => {
					return (
						<Chip
							color="primary"
							variant={row.isOutbound ? "outlined" : "filled"}
							size="small"
							label={row.isOutbound ? "outbound" : "inbound"}
						/>
					);
				},
			},
			{
				header: {
					id: "status",
					label: "Status",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "status",
				getCellValue: (row: any) => {
					return (
						<Tooltip title={row?.metadata?.reason} arrow>
							<Chip
								color={CALL_STATUS_COLORS[row.status]}
								variant="outlined"
								size="small"
								label={CALL_STATUS_LABELS[row.status]}
							/>
						</Tooltip>
					);
				},
			},
			{
				header: {
					id: "summary",
					label: "Summary",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "summary",
				getCellValue: (row: any) => {
					return (
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							{row?.metadata?.summary && (
								<Tooltip
									title={row?.metadata?.summary}
									arrow
									slotProps={{ tooltip: { sx: { lineHeight: 1.8, maxWidth: 400 } } }}
								>
									<IconButton size="small" color="primary">
										<InfoIcon width={20} height={20} weight="duotone" color="var(--mui-palette-primary-main)" />
									</IconButton>
								</Tooltip>
							)}
							{row?.metadata?.enqueuedForAnalysis && <AnalysingIndicator />}
						</Box>
					);
				},
			},
			{
				header: {
					id: "disposition",
					label: "Disposition",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "disposition",
				getCellValue: (row: any) => {
					return row?.metadata?.disposition;
				},
			},
			{
				header: {
					id: "createdAt",
					label: "Date",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "createdAt",
				getCellValue: (row: any) => formatDate(row.createdAt),
			},
			{
				header: {
					id: "actions",
					label: "",
					width: 100,
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "actions",
				getCellValue: (row: any) => {
          const hasStuckAction = STACK_STATUSES.includes(row?.status) && permissions?.unstuck;
          const hasConversationAction = row?.conversationId;

          const allActionsAreHidden = !hasConversationAction && !hasStuckAction;

          if (allActionsAreHidden) {
            return null;
          }
        
					return (
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
							<TableActions
								actions={[
									{
                    hidden: !hasConversationAction,
										label: "Play recording",
										icon: <PlayCircleIcon width={20} height={20} weight="fill" color="var(--mui-palette-info-light)" />,
										onClick: () => handleRecordingModalOpen(row),
									},
									{
                    hidden: !hasConversationAction,
										label: "View conversation logs",
										icon: <ChatsIcon width={20} height={20} weight="fill" color="var(--mui-palette-primary-main)" />,
										onClick: () => handleCallLogModalOpen(row),
									},
									{
										hidden: !user?.isSuperAdmin,
										label: "View completions JSON",
										icon: <InfoIcon width={20} height={20} weight="fill" color="var(--mui-palette-info-main)" />,
										onClick: () => setSelectedConversationId(String(row.conversationId)),
									},
									{
                    hidden: !hasStuckAction,
										label: "Mark as stuck",
										onClick: () => {
											setStuckCallId(row.id);
											setIsConfirmOpen(true);
										},
										icon: <WarningIcon width={20} height={20} weight="fill" color="var(--mui-palette-warning-main)" />,
									},
								]}
							/>
						</Box>
					);
				},
			},
		];
	}, [navigate, permissions?.unstuck, handleRecordingModalOpen, handleCallLogModalOpen]);

	const statisticCards = React.useMemo(() => {
		return [
			{
				title: "Total Call Logs",
				value: stats?.callsCount,
				icon: <PhoneCall color="var(--mui-palette-primary-main)" width={20} height={20} />,
				additonalInfo: "",
			},
			{
				title: "Avg Call Duration",
				value: +(stats?.avgDuration / 60).toFixed(2) || 0,
				icon: <Clock color="var(--mui-palette-success-main)" width={20} height={20} />,
				additonalInfo: "Minutes per call",
			},
			{
				title: "Total Calls Minutes",
				value: +(stats?.totalDuration / 60).toFixed(2) || 0,
				icon: <Phone color="var(--mui-palette-error-main)" width={20} height={20} />,
				additonalInfo: "Minutes of AI conversation",
			},
			// {
			// 	title: "AI Confidence",
			// 	value: 3,
			// 	icon: <Lightning color="var(--mui-palette-warning-main)" width={20} height={20} />,
			// 	additonalInfo: "Across all call logs",
			// },
		];
	}, [stats]);

	const animationDelay = 150;

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={3}
						sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
					>
						<Box sx={{ flex: "1 1 auto" }}>
							<Typography variant="h4">Call Logs</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								View all call logs
							</Typography>
						</Box>
					</Stack>

					<Grid container spacing={3}>
						{statisticCards.map((card, index) => (
							<Grid
								key={index}
								size={{
									md: 3,
									sm: 6,
									xs: 12,
								}}
							>
								<StatisticCard
									{...card}
									isLoading={isStatsLoading}
									animationDelay={index * animationDelay}
									timeout={animationDelay * statisticCards.length}
									isLast={index === statisticCards.length - 1}
								/>
							</Grid>
						))}
					</Grid>

					<TableAnimation delay={450}>
						<Card>
							<CustomTable
								columns={columns}
								data={callLogs}
								refetchData={fetchCallLogs}
								isLoading={isLoading}
								totalItems={totalItems}
								filtersOptions={filtersOptions}
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>

			<CallLogModal open={isCallLogModalOpen} onClose={handleCallLogModalClose} callLog={selectedCallLog || null} />

			<RecordingModal
				open={isRecordingModalOpen}
				onClose={handleRecordingModalClose}
				callLog={selectedRecordingCallLog || null}
			/>

			<ConfirmModal
				open={isConfirmOpen}
				onClose={() => {
					setIsConfirmOpen(false);
					setStuckCallId(null);
				}}
				onSumbit={async () => {
					if (!stuckCallId) return;
					await markCallStuckMutation.mutateAsync(stuckCallId);
					setStuckCallId(null);
					fetchCallLogs();
				}}
				title="Mark call as stuck"
				description="Are you sure you want to mark this call as stuck?"
			/>

			<CompletionsJsonModal
				open={Boolean(selectedConversationId)}
				conversationId={selectedConversationId}
				onClose={() => setSelectedConversationId(null)}
			/>
		</React.Fragment>
	);
}
